/**
 * SymbologyEngine Service
 *
 * Provides categorized symbology, graduated symbology, and feature labeling
 * capabilities for vector layers. Generates OpenLayers style functions and
 * legend items based on symbology configuration.
 *
 * **Validates: Requirements 41, 42, 43** - Categorized Symbology, Graduated Symbology, Feature Labeling
 */

import { Style, Fill, Stroke, Circle, RegularShape, Text } from 'ol/style';
import type Feature from 'ol/Feature';
import type { Geometry } from 'ol/geom';
import type {
  Symbology,
  CategorizedRule,
  GraduatedRule,
  SymbolStyle,
  LabelConfig,
  LegendItem,
  ClassificationMethod,
  ColorRamp,
  CategoryGroup,
  MarkerSymbol,
  LabelPlacement,
} from '../types/symbology';
import { PREDEFINED_COLOR_RAMPS } from '../types/symbology';

// ---------------------------------------------------------------------------
// Color utilities
// ---------------------------------------------------------------------------

/**
 * Converts a hex color string to an rgba string with the given opacity.
 */
function hexToRgba(hex: string, opacity: number): string {
  let h = hex.replace('#', '');
  if (h.length === 3) {
    h = h
      .split('')
      .map((c) => c + c)
      .join('');
  }
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

/**
 * Interpolates colors from a color ramp to produce the requested number of colors.
 */
export function interpolateColorRamp(ramp: ColorRamp, numClasses: number): string[] {
  const { colors } = ramp;
  if (numClasses <= 1) return [colors[0]];
  if (numClasses >= colors.length) {
    // If more classes than ramp colors, interpolate between stops
    const result: string[] = [];
    for (let i = 0; i < numClasses; i++) {
      const t = i / (numClasses - 1);
      const idx = t * (colors.length - 1);
      const lower = Math.floor(idx);
      const upper = Math.min(Math.ceil(idx), colors.length - 1);
      const frac = idx - lower;
      result.push(lerpColor(colors[lower], colors[upper], frac));
    }
    return result;
  }
  // Select evenly spaced colors from the ramp
  const result: string[] = [];
  for (let i = 0; i < numClasses; i++) {
    const idx = Math.round((i / (numClasses - 1)) * (colors.length - 1));
    result.push(colors[idx]);
  }
  return result;
}

/**
 * Linearly interpolates between two hex colors.
 */
function lerpColor(color1: string, color2: string, t: number): string {
  const c1 = hexToRgb(color1);
  const c2 = hexToRgb(color2);
  const r = Math.round(c1.r + (c2.r - c1.r) * t);
  const g = Math.round(c1.g + (c2.g - c1.g) * t);
  const b = Math.round(c1.b + (c2.b - c1.b) * t);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  let h = hex.replace('#', '');
  if (h.length === 3)
    h = h
      .split('')
      .map((c) => c + c)
      .join('');
  return {
    r: parseInt(h.substring(0, 2), 16),
    g: parseInt(h.substring(2, 4), 16),
    b: parseInt(h.substring(4, 6), 16),
  };
}

// ---------------------------------------------------------------------------
// Auto-color generation for categorized symbology
// ---------------------------------------------------------------------------

/**
 * Generates a set of distinct colors for categorized symbology.
 * Uses a golden-angle hue rotation for maximum visual distinction.
 */
export function generateCategoryColors(count: number): string[] {
  const colors: string[] = [];
  const goldenAngle = 137.508;
  for (let i = 0; i < count; i++) {
    const hue = (i * goldenAngle) % 360;
    const saturation = 65 + (i % 3) * 10;
    const lightness = 45 + (i % 2) * 10;
    colors.push(hslToHex(hue, saturation, lightness));
  }
  return colors;
}

function hslToHex(h: number, s: number, l: number): string {
  s /= 100;
  l /= 100;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color)
      .toString(16)
      .padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

// ---------------------------------------------------------------------------
// Classification methods
// ---------------------------------------------------------------------------

/**
 * Classifies numeric values into ranges using the specified method.
 * Returns an array of break points (length = numClasses + 1).
 *
 * **Validates: Requirement 42.3** - Classification methods
 */
export function classifyValues(
  values: number[],
  method: ClassificationMethod,
  numClasses: number
): number[] {
  const sorted = values.filter(Number.isFinite).sort((a, b) => a - b);
  if (sorted.length === 0) return [];

  const min = sorted[0];
  const max = sorted[sorted.length - 1];
  if (min === max) return [min, max];

  const requestedClasses = Number.isFinite(numClasses) ? Math.max(1, Math.floor(numClasses)) : 1;

  switch (method) {
    case 'equal-interval':
      return equalIntervalBreaks(min, max, requestedClasses);
    case 'quantile':
      return quantileBreaks(sorted, requestedClasses);
    case 'natural-breaks':
      return naturalBreaks(sorted, requestedClasses);
    case 'standard-deviation':
      return standardDeviationBreaks(sorted, requestedClasses);
    default:
      return equalIntervalBreaks(min, max, requestedClasses);
  }
}

/**
 * Equal interval classification: divides the range into equal-width intervals.
 */
function equalIntervalBreaks(min: number, max: number, numClasses: number): number[] {
  const breaks: number[] = [min];
  for (let i = 1; i < numClasses; i++) {
    const ratio = i / numClasses;
    // Convex interpolation avoids overflowing max - min for very large finite values.
    breaks.push(min * (1 - ratio) + max * ratio);
  }
  breaks.push(max);
  return breaks;
}

/**
 * Quantile classification: each class contains approximately the same number of values.
 */
function quantileBreaks(sorted: number[], numClasses: number): number[] {
  const breaks: number[] = [sorted[0]];
  for (let i = 1; i < numClasses; i++) {
    const idx = Math.round((i / numClasses) * sorted.length) - 1;
    breaks.push(sorted[Math.max(0, idx)]);
  }
  breaks.push(sorted[sorted.length - 1]);
  return breaks;
}

/**
 * Natural breaks (Jenks) classification: minimizes within-class variance.
 * Uses a simplified Fisher-Jenks algorithm.
 */
function naturalBreaks(sorted: number[], numClasses: number): number[] {
  const n = sorted.length;
  if (n <= numClasses) {
    const breaks = [sorted[0]];
    for (let i = 1; i < n; i++) {
      breaks.push(sorted[i]);
    }
    return breaks;
  }

  // Initialize matrices
  const lowerClassLimits: number[][] = Array.from({ length: n + 1 }, () =>
    new Array(numClasses + 1).fill(0)
  );
  const varianceCombinations: number[][] = Array.from({ length: n + 1 }, () =>
    new Array(numClasses + 1).fill(Infinity)
  );

  for (let i = 1; i <= numClasses; i++) {
    lowerClassLimits[1][i] = 1;
    varianceCombinations[1][i] = 0;
  }

  for (let l = 2; l <= n; l++) {
    let sum = 0;
    let sumSquares = 0;

    for (let m = 1; m <= l; m++) {
      const lowerClassLimit = l - m + 1;
      const val = sorted[lowerClassLimit - 1];
      sum += val;
      sumSquares += val * val;

      const variance = sumSquares - (sum * sum) / m;
      const idx = lowerClassLimit - 1;

      if (idx !== 0) {
        for (let j = 2; j <= numClasses; j++) {
          const newVariance = variance + varianceCombinations[idx][j - 1];
          if (newVariance < varianceCombinations[l][j]) {
            lowerClassLimits[l][j] = lowerClassLimit;
            varianceCombinations[l][j] = newVariance;
          }
        }
      }
    }

    lowerClassLimits[l][1] = 1;
    varianceCombinations[l][1] = sumSquares - (sum * sum) / l;
  }

  // Extract breaks
  const breaks: number[] = new Array(numClasses + 1);
  breaks[numClasses] = sorted[n - 1];
  breaks[0] = sorted[0];

  let k = n;
  for (let j = numClasses; j >= 2; j--) {
    const id = lowerClassLimits[k][j] - 2;
    breaks[j - 1] = sorted[id];
    k = lowerClassLimits[k][j] - 1;
  }

  return breaks;
}

/**
 * Standard deviation classification: classes based on standard deviations from the mean.
 */
function standardDeviationBreaks(sorted: number[], numClasses: number): number[] {
  const n = sorted.length;
  const mean = sorted.reduce((sum, v) => sum + v, 0) / n;
  const variance = sorted.reduce((sum, v) => sum + (v - mean) ** 2, 0) / n;
  const stdDev = Math.sqrt(variance);

  const breaks: number[] = [];
  const halfClasses = Math.floor(numClasses / 2);

  for (let i = -halfClasses; i <= halfClasses; i++) {
    breaks.push(mean + i * stdDev);
  }

  // Ensure we cover the full range
  breaks[0] = Math.min(breaks[0], sorted[0]);
  breaks[breaks.length - 1] = Math.max(breaks[breaks.length - 1], sorted[n - 1]);

  // Trim to numClasses + 1 breaks
  while (breaks.length > numClasses + 1) {
    breaks.pop();
  }
  while (breaks.length < numClasses + 1) {
    breaks.push(breaks[breaks.length - 1] + stdDev);
  }
  breaks[breaks.length - 1] = Math.max(breaks[breaks.length - 1], sorted[n - 1]);

  return breaks;
}

// ---------------------------------------------------------------------------
// Unique value extraction
// ---------------------------------------------------------------------------

/**
 * Extracts unique attribute values from a set of features for a given field.
 * **Validates: Requirement 41.2**
 */
export function getUniqueValues(
  features: Array<{ properties: Record<string, any> }>,
  field: string
): unknown[] {
  const seen = new Set<string>();
  const values: unknown[] = [];
  for (const feature of features) {
    const val = feature.properties?.[field];
    if (val !== undefined && val !== null) {
      const key = String(val);
      if (!seen.has(key)) {
        seen.add(key);
        values.push(val);
      }
    }
  }
  return values;
}

/**
 * Extracts numeric values from features for a given field.
 * Filters out non-numeric and null values.
 */
export function getNumericValues(
  features: Array<{ properties: Record<string, any> }>,
  field: string
): number[] {
  const values: number[] = [];
  for (const feature of features) {
    const rawValue = feature.properties?.[field];
    if (rawValue === null || rawValue === undefined || rawValue === '') continue;
    const value = Number(rawValue);
    if (Number.isFinite(value)) values.push(value);
  }
  return values;
}

// ---------------------------------------------------------------------------
// Categorized symbology rule generation
// ---------------------------------------------------------------------------

/**
 * Generates categorized rules by assigning unique colors to unique attribute values.
 * **Validates: Requirements 41.2, 41.3**
 */
export function generateCategorizedRules(
  features: Array<{ properties: Record<string, any> }>,
  field: string,
  customColors?: Record<string, string>
): CategorizedRule[] {
  const uniqueValues = getUniqueValues(features, field);
  const colors = generateCategoryColors(uniqueValues.length);

  return uniqueValues.map((value, index) => {
    const colorKey = String(value);
    const fillColor = customColors?.[colorKey] || colors[index];
    return {
      attributeField: field,
      value,
      symbol: {
        fillColor,
        strokeColor: '#ffffff',
        strokeWidth: 1,
        opacity: 1,
      },
      label: String(value),
    };
  });
}

/**
 * Applies category grouping: assigns the same symbol to multiple values.
 * **Validates: Requirement 41.6**
 */
export function applyCategoryGrouping(
  rules: CategorizedRule[],
  groups: CategoryGroup[]
): CategorizedRule[] {
  const groupedRules: CategorizedRule[] = [];
  const groupedValues = new Set<string>();

  // Add grouped rules
  for (const group of groups) {
    for (const value of group.values) {
      groupedValues.add(String(value));
      groupedRules.push({
        attributeField: rules[0]?.attributeField || '',
        value,
        symbol: group.symbol,
        label: group.name,
      });
    }
  }

  // Add remaining ungrouped rules
  for (const rule of rules) {
    if (!groupedValues.has(String(rule.value))) {
      groupedRules.push(rule);
    }
  }

  return groupedRules;
}

// ---------------------------------------------------------------------------
// Graduated symbology rule generation
// ---------------------------------------------------------------------------

/**
 * Generates graduated rules by classifying numeric values into ranges.
 * **Validates: Requirements 42.2, 42.3, 42.4, 42.5**
 */
export function generateGraduatedRules(
  features: Array<{ properties: Record<string, any> }>,
  field: string,
  method: ClassificationMethod,
  numClasses: number,
  colorRampId: string
): GraduatedRule[] {
  const values = getNumericValues(features, field);
  if (values.length === 0) return [];

  const sortedDistinctValues = [...new Set(values)].sort((a, b) => a - b);
  const requestedClasses = Number.isFinite(numClasses) ? Math.max(1, Math.floor(numClasses)) : 1;
  const effectiveClasses = Math.min(requestedClasses, sortedDistinctValues.length);

  let breaks = classifyValues(values, method, effectiveClasses);
  const hasExpectedFiniteOrderedBreaks =
    breaks.length === effectiveClasses + 1 &&
    breaks.every(Number.isFinite) &&
    breaks.every((value, index) => index === 0 || value >= breaks[index - 1]);

  if (!hasExpectedFiniteOrderedBreaks && sortedDistinctValues.length > 1) {
    breaks = equalIntervalBreaks(
      sortedDistinctValues[0],
      sortedDistinctValues[sortedDistinctValues.length - 1],
      effectiveClasses
    );
  }

  // Duplicate quantile/Jenks boundaries do not create meaningful additional classes.
  const orderedBreaks = breaks.filter(
    (value, index) => Number.isFinite(value) && (index === 0 || value > breaks[index - 1])
  );
  if (sortedDistinctValues.length === 1) {
    orderedBreaks.splice(0, orderedBreaks.length, sortedDistinctValues[0], sortedDistinctValues[0]);
  }
  if (orderedBreaks.length < 2) return [];

  const ruleCount = orderedBreaks.length - 1;
  const ramp =
    PREDEFINED_COLOR_RAMPS.find((r) => r.id === colorRampId) || PREDEFINED_COLOR_RAMPS[0];
  const colors = interpolateColorRamp(ramp, ruleCount);

  return Array.from({ length: ruleCount }, (_, index) => {
    const minValue = orderedBreaks[index];
    const maxValue = orderedBreaks[index + 1];
    return {
      attributeField: field,
      minValue,
      maxValue,
      symbol: {
        fillColor: colors[index],
        strokeColor: '#ffffff',
        strokeWidth: 1,
        opacity: 1,
      },
      label: `${minValue.toFixed(2)} – ${maxValue.toFixed(2)}`,
    };
  });
}

// ---------------------------------------------------------------------------
// Legend generation
// ---------------------------------------------------------------------------

/**
 * Generates legend items from a symbology configuration.
 * **Validates: Requirements 41.5, 42.6**
 */
export function generateLegend(symbology: Symbology): LegendItem[] {
  if (symbology.type === 'categorized' && symbology.categorizedRules) {
    return symbology.categorizedRules.map((rule) => ({
      label: rule.label,
      symbol: rule.symbol,
    }));
  }

  if (symbology.type === 'graduated' && symbology.graduatedRules) {
    return symbology.graduatedRules.map((rule) => ({
      label: rule.label,
      symbol: rule.symbol,
    }));
  }

  // Simple symbology - single legend item
  return [
    {
      label: 'Default',
      symbol: {
        fillColor: symbology.fillColor,
        strokeColor: symbology.strokeColor,
        strokeWidth: symbology.strokeWidth,
        opacity: symbology.opacity,
      },
    },
  ];
}

// ---------------------------------------------------------------------------
// Label expression evaluation
// ---------------------------------------------------------------------------

/**
 * Evaluates a label expression by replacing field references with actual values.
 * Expression format: '{fieldName}' placeholders are replaced with property values.
 * **Validates: Requirement 43.6**
 */
export function evaluateLabelExpression(
  expression: string,
  properties: Record<string, any>
): string {
  return expression.replace(/\{([^}]+)\}/g, (_, fieldName) => {
    const value = properties[fieldName.trim()];
    return value !== undefined && value !== null ? String(value) : '';
  });
}

/**
 * Gets the label text for a feature based on the label configuration.
 */
export function getLabelText(labelConfig: LabelConfig, properties: Record<string, any>): string {
  if (labelConfig.expression) {
    return evaluateLabelExpression(labelConfig.expression, properties);
  }
  const value = properties[labelConfig.field];
  return value !== undefined && value !== null ? String(value) : '';
}

// ---------------------------------------------------------------------------
// OpenLayers style function generation
// ---------------------------------------------------------------------------

/**
 * Creates an OpenLayers marker image for point features.
 */
function createMarkerImage(
  markerSymbol: MarkerSymbol,
  radius: number,
  fillColor: string,
  strokeColor: string,
  strokeWidth: number
): Circle | RegularShape {
  const fill = new Fill({ color: fillColor });
  const stroke = new Stroke({ color: strokeColor, width: strokeWidth });

  switch (markerSymbol) {
    case 'circle':
      return new Circle({ radius, fill, stroke });
    case 'square':
      return new RegularShape({ points: 4, radius, angle: Math.PI / 4, fill, stroke });
    case 'triangle':
      return new RegularShape({ points: 3, radius, angle: 0, fill, stroke });
    case 'star':
      return new RegularShape({ points: 5, radius, radius2: radius * 0.4, angle: 0, fill, stroke });
    case 'cross':
      return new RegularShape({
        points: 4,
        radius,
        radius2: 0,
        angle: 0,
        fill: new Fill({ color: strokeColor }),
        stroke,
      });
    case 'x':
      return new RegularShape({
        points: 4,
        radius,
        radius2: 0,
        angle: Math.PI / 4,
        fill: new Fill({ color: strokeColor }),
        stroke,
      });
    default:
      return new Circle({ radius, fill, stroke });
  }
}

/**
 * Creates an OpenLayers Text style from a LabelConfig.
 * **Validates: Requirement 43.3** - Label style customization
 */
function createTextStyle(labelConfig: LabelConfig, text: string): Text {
  const textStyle = new Text({
    text,
    font: `${labelConfig.fontSize}px ${labelConfig.font}`,
    fill: new Fill({ color: labelConfig.color }),
    stroke: labelConfig.haloColor
      ? new Stroke({ color: labelConfig.haloColor, width: labelConfig.haloWidth || 2 })
      : undefined,
    overflow: true,
    placement: labelConfig.placement === 'line' ? 'line' : 'point',
  });

  return textStyle;
}

/**
 * Creates an OpenLayers Style from a SymbolStyle configuration.
 */
function createStyleFromSymbol(
  symbol: SymbolStyle,
  geometryType: 'Point' | 'LineString' | 'Polygon',
  opacity: number = 1
): Style {
  const fillColor = symbol.fillColor || '#3388ff';
  const strokeColor = symbol.strokeColor || (geometryType === 'LineString' ? '#3388ff' : '#ffffff');
  const strokeWidth = symbol.strokeWidth ?? (geometryType === 'LineString' ? 3 : 1);
  const effectiveOpacity = symbol.opacity ?? opacity;
  const size = symbol.size || 8;

  const fillRgba = hexToRgba(fillColor, effectiveOpacity);
  const strokeRgba = hexToRgba(strokeColor, effectiveOpacity);

  const styleOptions: any = {};

  if (geometryType !== 'LineString') {
    styleOptions.fill = new Fill({ color: fillRgba });
  }
  styleOptions.stroke = new Stroke({ color: strokeRgba, width: strokeWidth });

  if (geometryType === 'Point') {
    styleOptions.image = createMarkerImage('circle', size, fillRgba, strokeRgba, strokeWidth);
  }

  return new Style(styleOptions);
}

/**
 * Creates an OpenLayers style function for a layer based on its symbology configuration.
 * This function handles categorized, graduated, and simple symbology, plus labeling.
 *
 * **Validates: Requirements 41.3, 42.5, 43.1**
 */
export function createStyleFunction(
  symbology: Symbology,
  geometryType: 'Point' | 'LineString' | 'Polygon'
): (feature: Feature<Geometry>, resolution: number) => Style | Style[] {
  return (feature: Feature<Geometry>, _resolution: number): Style | Style[] => {
    const properties = feature.getProperties();
    let baseStyle: Style;

    if (symbology.type === 'categorized' && symbology.categorizedRules) {
      const matchedRule = symbology.categorizedRules.find(
        (rule) => properties[rule.attributeField] == rule.value
      );
      baseStyle = matchedRule
        ? createStyleFromSymbol(matchedRule.symbol, geometryType, symbology.opacity)
        : createDefaultStyle(symbology, geometryType);
    } else if (symbology.type === 'graduated' && symbology.graduatedRules) {
      const field = symbology.graduatedRules[0]?.attributeField;
      const numValue = field ? Number(properties[field]) : NaN;
      const matchedRule = !isNaN(numValue)
        ? symbology.graduatedRules.find(
            (rule) => numValue >= rule.minValue && numValue <= rule.maxValue
          )
        : undefined;
      baseStyle = matchedRule
        ? createStyleFromSymbol(matchedRule.symbol, geometryType, symbology.opacity)
        : createDefaultStyle(symbology, geometryType);
    } else {
      baseStyle = createDefaultStyle(symbology, geometryType);
    }

    // Apply labeling if configured
    if (symbology.labelConfig?.enabled) {
      const labelText = getLabelText(symbology.labelConfig, properties);
      if (labelText) {
        const textStyle = createTextStyle(symbology.labelConfig, labelText);
        baseStyle.setText(textStyle);
      }
    }

    return baseStyle;
  };
}

/**
 * Creates a default OpenLayers Style from the simple symbology fields.
 */
function createDefaultStyle(
  symbology: Symbology,
  geometryType: 'Point' | 'LineString' | 'Polygon'
): Style {
  const fillColor = symbology.fillColor || '#3388ff';
  const strokeColor =
    symbology.strokeColor || (geometryType === 'LineString' ? '#3388ff' : '#ffffff');
  const strokeWidth = symbology.strokeWidth ?? (geometryType === 'LineString' ? 3 : 2);
  const opacity = symbology.opacity ?? 1;

  const fillRgba = hexToRgba(fillColor, opacity);
  const strokeRgba = hexToRgba(strokeColor, opacity);

  const styleOptions: any = {};

  if (geometryType !== 'LineString') {
    styleOptions.fill = new Fill({ color: fillRgba });
  }
  styleOptions.stroke = new Stroke({ color: strokeRgba, width: strokeWidth });

  if (geometryType === 'Point') {
    const markerSymbol = (symbology.markerSymbol || 'circle') as MarkerSymbol;
    const markerSize = symbology.markerSize || 8;
    styleOptions.image = createMarkerImage(
      markerSymbol,
      markerSize,
      fillRgba,
      strokeRgba,
      strokeWidth
    );
  }

  return new Style(styleOptions);
}

// ---------------------------------------------------------------------------
// Main SymbologyEngine class
// ---------------------------------------------------------------------------

/**
 * SymbologyEngine provides methods for applying categorized, graduated,
 * and label-based symbology to vector layers.
 *
 * **Validates: Requirements 41, 42, 43**
 */
export class SymbologyEngine {
  /**
   * Generates categorized symbology rules for a layer.
   * Identifies unique values in the specified field and assigns colors.
   *
   * **Validates: Requirements 41.1, 41.2, 41.3**
   */
  generateCategorizedSymbology(
    features: Array<{ properties: Record<string, any> }>,
    field: string,
    customColors?: Record<string, string>
  ): Symbology {
    const rules = generateCategorizedRules(features, field, customColors);
    return {
      type: 'categorized',
      categorizedRules: rules,
      opacity: 1,
    };
  }

  /**
   * Generates graduated symbology rules for a layer.
   * Classifies numeric values into ranges and assigns colors from a ramp.
   *
   * **Validates: Requirements 42.1, 42.2, 42.3, 42.4, 42.5**
   */
  generateGraduatedSymbology(
    features: Array<{ properties: Record<string, any> }>,
    field: string,
    method: ClassificationMethod,
    numClasses: number,
    colorRampId: string
  ): Symbology {
    const rules = generateGraduatedRules(features, field, method, numClasses, colorRampId);
    return {
      type: 'graduated',
      graduatedRules: rules,
      opacity: 1,
    };
  }

  /**
   * Creates a label configuration for a layer.
   *
   * **Validates: Requirements 43.1, 43.2, 43.3**
   */
  createLabelConfig(config: Partial<LabelConfig> & { field: string }): LabelConfig {
    return {
      enabled: true,
      font: config.font || 'Arial',
      fontSize: config.fontSize || 12,
      color: config.color || '#000000',
      haloColor: config.haloColor || '#ffffff',
      haloWidth: config.haloWidth || 2,
      placement: config.placement || 'point',
      expression: config.expression,
      declutter: config.declutter ?? true,
      field: config.field,
    };
  }

  /**
   * Generates legend items from a symbology configuration.
   *
   * **Validates: Requirements 41.5, 42.6**
   */
  generateLegend(symbology: Symbology): LegendItem[] {
    return generateLegend(symbology);
  }

  /**
   * Classifies numeric values using the specified method.
   *
   * **Validates: Requirement 42.3**
   */
  classifyValues(values: number[], method: ClassificationMethod, numClasses: number): number[] {
    return classifyValues(values, method, numClasses);
  }

  /**
   * Gets unique values from features for a given field.
   *
   * **Validates: Requirement 41.2**
   */
  getUniqueValues(features: Array<{ properties: Record<string, any> }>, field: string): unknown[] {
    return getUniqueValues(features, field);
  }

  /**
   * Creates an OpenLayers style function for the given symbology.
   */
  createStyleFunction(
    symbology: Symbology,
    geometryType: 'Point' | 'LineString' | 'Polygon'
  ): (feature: Feature<Geometry>, resolution: number) => Style | Style[] {
    return createStyleFunction(symbology, geometryType);
  }

  /**
   * Applies category grouping to existing categorized rules.
   *
   * **Validates: Requirement 41.6**
   */
  applyCategoryGrouping(rules: CategorizedRule[], groups: CategoryGroup[]): CategorizedRule[] {
    return applyCategoryGrouping(rules, groups);
  }

  /**
   * Gets available color ramps.
   */
  getColorRamps(): ColorRamp[] {
    return PREDEFINED_COLOR_RAMPS;
  }

  /**
   * Evaluates a label expression for a feature.
   *
   * **Validates: Requirement 43.6**
   */
  evaluateLabelExpression(expression: string, properties: Record<string, any>): string {
    return evaluateLabelExpression(expression, properties);
  }
}

// Export a singleton instance
export const symbologyEngine = new SymbologyEngine();
export default symbologyEngine;
